import cn from 'classnames';
import { formatCurrency } from 'common/FormatUtilities';
import { match } from 'ts-pattern';
import Button, { ButtonType } from 'web-common/src/shared/design-system/components/Button';
import { Icon, IconColor } from 'web-common/src/shared/design-system/components/Icon';
import { DishModalContent } from './DishModalContent';

interface DishModalFooterProps {
  onQuantityChange: (quantity: number) => void;
  quantity: number;
  minQuantity: number | undefined;
  maxQuantity: number | undefined;
  isServingSizeSelected: boolean;
  isEditingCart: boolean;
  hasBeenUpdated: boolean;
  actionButtonClick?: () => void;
  priceToDisplay?: number | null;
}

type ModalFooterState = {
  actionButtonDisabled: boolean;
  actionButtonText: string;
};

const getModalFooterState = (
  input: Pick<
    DishModalFooterProps,
    'isServingSizeSelected' | 'isEditingCart' | 'hasBeenUpdated' | 'quantity' | 'maxQuantity'
  >
): ModalFooterState => {
  const actionButtonText = match(input)
    .with({ isEditingCart: true, quantity: 0 }, () => 'Remove')
    .with({ isEditingCart: true }, () => 'Update')
    .with({ isServingSizeSelected: false }, () => 'Select portion size')
    .otherwise(() => 'Add to order');

  const { isServingSizeSelected, isEditingCart, hasBeenUpdated, quantity, maxQuantity } = input;
  return {
    actionButtonDisabled:
      !isServingSizeSelected ||
      (isEditingCart && !hasBeenUpdated) ||
      (maxQuantity !== undefined && quantity > maxQuantity),
    actionButtonText,
  };
};

export const DishModalFooter: React.FC<DishModalFooterProps> = ({
  quantity,
  onQuantityChange,
  minQuantity,
  maxQuantity,
  isServingSizeSelected,
  actionButtonClick,
  isEditingCart,
  hasBeenUpdated,
  priceToDisplay = null,
}) => {
  const footerState = getModalFooterState({
    isServingSizeSelected,
    isEditingCart,
    hasBeenUpdated,
    quantity,
    maxQuantity,
  });

  const decrementDisabled = quantity === 0 || (minQuantity !== undefined && quantity <= minQuantity);
  const incrementDisabled = maxQuantity !== undefined && quantity >= maxQuantity;

  const getButtonClassName = (isDisabled: boolean) =>
    cn('flex items-center justify-center rounded p-1', {
      'bg-neutral-300': isDisabled,
      'bg-raspberry': !isDisabled,
    });

  const quantityPicker = (
    <div className='flex w-[108px] items-center justify-between rounded bg-milk font-semibold text-eggplant'>
      <button
        disabled={decrementDisabled}
        onClick={() => onQuantityChange(quantity - 1)}
        className={getButtonClassName(decrementDisabled)}>
        <Icon name='minusHandWritten' size={16} color={IconColor.WHITE} />
      </button>
      <span className='text-h5-bold'>{quantity}</span>
      <button
        disabled={incrementDisabled}
        onClick={() => onQuantityChange(quantity + 1)}
        className={getButtonClassName(incrementDisabled)}>
        <Icon name='plusHandWritten' size={16} color={IconColor.WHITE} />
      </button>
    </div>
  );

  const buttonContent = (
    <span className='flex w-full items-center justify-between gap-2'>
      <span>{footerState.actionButtonText}</span>
      {(priceToDisplay ?? 0) > 0 && <span className='rounded'>+{formatCurrency(priceToDisplay, true)}</span>}
    </span>
  );

  return (
    <div
      className={cn(
        'sticky inset-x-0 bottom-0 z-drawer border-t border-solid border-eggplant-50 bg-white py-3',
        'shadow-footer-drop'
      )}>
      <DishModalContent className='flex w-full justify-between'>
        {quantityPicker}

        {/* Action Button */}
        <div>
          <Button
            buttonType={ButtonType.Primary}
            onClick={actionButtonClick}
            disabled={footerState.actionButtonDisabled}>
            {buttonContent}
          </Button>
        </div>
      </DishModalContent>
    </div>
  );
};
